import { OPERATORS } from "@miplatform/constants";

/**
 * Class representing a client filter.
 */
export class ClientFilter {
  /**
   * Create a client filter.
   * @param {Array} data - The original data to be filtered.
   * @param {Array<string>} searchableFields - The fields that can be searched.
   * @param {Function} setFilteredData - Callback to set the filtered data.
   */
  constructor(data, searchableFields, setFilteredData) {
    this.originalData = data || [];
    this.filteredData = data || [];
    this.sortColumn = null;
    this.sortDirection = null;
    this.filters = [];
    this.searchableFields = searchableFields;
    this.setFilteredData = setFilteredData;
    this.groupByFields = [];
    this.groupedData = null;
    this.initialize(data);
  }

  initialize(data) {
    if (this.setFilteredData) {
      this.setFilteredData(data);
    }
  }

  /**
   * Handle search input.
   * @param {string} searchText - The text to search for.
   */
  handleSearch = (searchText) => {
    this.filters = this.filters.filter((filter) => filter.field !== "search");
    if (searchText) {
      this.handleFilter({
        field: "search",
        operator: OPERATORS.CONTAINS,
        value: searchText.toLowerCase(),
      });
    }
    this.filterData();
  };

  /**
   * Handle adding or updating a filter.
   * @param {Object} filter - The filter to add or update.
   * @param {string} filter.field - The field to filter on.
   * @param {string} filter.operator - The operator to use for filtering.
   * @param {*} filter.value - The value to filter by.
   * @param {*} [filter.maxValue] - The maximum value for range filters.
   */
  handleFilter = (filter) => {
    const existingFilter = this.filters.find((f) => f.field === filter.field);
    if (existingFilter) {
      existingFilter.operator = filter.operator;
      existingFilter.value = filter.value;
      if (filter.maxValue !== undefined) {
        existingFilter.maxValue = filter.maxValue;
      }
    } else {
      this.filters.push(filter);
    }
    this.filterData();
  };

  /**
   * Remove a filter.
   * @param {string} field - The field of the filter to remove.
   * @param {Function} [callback] - Optional callback to determine which filters to remove.
   */
  removeFilter = (field, callback) => {
    if (callback && typeof callback === "function") {
      this.filters = this.filters.filter((f) => !callback(f));
    } else {
      this.filters = this.filters.filter((f) => f.field !== field);
    }
    this.filterData();
  };

  /**
   * Remove all filters.
   */
  removeAllFilters = () => {
    this.filters = [];
    this.filterData();
  };

  /**
   * Update the original data.
   * @param {Array} newData - The new data to set.
   */
  updateData = (newData) => {
    this.originalData = newData;
    this.filterData();
  };

  /**
   * Filter the data based on the current filters.
   */
  filterData = () => {
    this.filteredData = this.originalData.filter((row) =>
      this.filters.every((filter) => {
        const { field, operator, value } = filter;

        if (field === "search") {
          return this.searchableFields.some((searchField) => {
            if (searchField.includes(" ")) {
              // Handle combined fields like "firstName lastName"
              const combinedFields = searchField.split(" ");
              const combinedValue = combinedFields
                .map((field) => row[field]?.toString().toLowerCase() || "")
                .join(" ")
                .trim();

              return combinedValue.includes(value.trim());
            }

            return row[searchField]
              ?.toString()
              .toLowerCase()
              .trim()
              .includes(value.trim().toLowerCase());
          });
        }

        const rowValue = row[field];
        // Convert to numbers for numeric comparisons
        const numericRowValue = parseFloat(rowValue);
        const numericFilterValue = parseFloat(value);
        const numericMaxValue =
          filter.maxValue !== undefined
            ? parseFloat(filter.maxValue)
            : undefined;

        // Use numeric comparison for numeric values, string comparison otherwise
        const useNumeric =
          !Number.isNaN(numericRowValue) &&
          !Number.isNaN(numericFilterValue) &&
          (numericMaxValue === undefined || !Number.isNaN(numericMaxValue));

        if (useNumeric) {
          switch (operator) {
            case OPERATORS.EQUALS:
              return numericRowValue === numericFilterValue;
            case OPERATORS.GREATER_THAN:
              return numericRowValue > numericFilterValue;
            case OPERATORS.LESS_THAN:
              return numericRowValue < numericFilterValue;
            case OPERATORS.GREATER_THAN_OR_EQUAL:
              return numericRowValue >= numericFilterValue;
            case OPERATORS.LESS_THAN_OR_EQUAL:
              return numericRowValue <= numericFilterValue;
            case OPERATORS.RANGE:
              return numericRowValue === numericFilterValue;
            case OPERATORS.IN_RANGE:
              return (
                numericRowValue >= numericFilterValue &&
                numericRowValue <= numericMaxValue
              );
            default:
              return ClientFilter.handleStringComparison(
                rowValue?.toString() || "",
                value.toString(),
                operator
              );
          }
        } else {
          return ClientFilter.handleStringComparison(
            rowValue?.toString() || "",
            value.toString(),
            operator
          );
        }
      })
    );

    // First sort the data if we have a sort column
    if (this.sortColumn) {
      this.sortData();
    }

    // If we have grouping fields, apply grouping after sorting
    if (this.groupByFields.length > 0) {
      this.applyGroupBy();
    } else if (this.setFilteredData) {
      this.setFilteredData(this.filteredData);
    }
  };

  /**
   * Group the filtered data by the specified fields
   * @param {Array<string>|string} fields - The fields to group by
   */
  handleGroupBy = (fields) => {
    // Convert single field to array for consistent handling
    let fieldArray = fields;
    if (!Array.isArray(fields)) {
      fieldArray = [fields];
    }

    // Filter out empty fields
    this.groupByFields = fieldArray.filter(
      (field) => field && field.trim() !== ""
    );

    if (this.groupByFields.length === 0) {
      // If no valid fields, remove grouping
      this.removeGroupBy();
    } else {
      // Apply grouping on the sorted data
      this.applyGroupBy();
    }
  };

  /**
   * Remove grouping and return to flat data view
   */
  removeGroupBy = () => {
    this.groupByFields = [];
    this.groupedData = null;

    // Update the callback with the flat data
    if (this.setFilteredData) {
      this.setFilteredData(this.filteredData);
    }
  };

  /**
   * Apply the groupBy operation to the filtered data
   */
  applyGroupBy = () => {
    if (this.groupByFields.length === 0) {
      this.groupedData = null;
      return;
    }

    // Group data hierarchically based on the groupByFields
    this.groupedData = this.groupDataByFields(
      this.filteredData,
      this.groupByFields
    );

    // Pass the grouped data to the callback
    if (this.setFilteredData) {
      this.setFilteredData(this.groupedData);
    }
  };

  /**
   * Groups data recursively by multiple fields
   * @param {Array} data - The data to group
   * @param {Array<string>} fields - The fields to group by (in order)
   * @returns {Array} - An array of group objects
   */
  groupDataByFields = (data, fields) => {
    if (!fields.length || !data.length) {
      return data;
    }

    const currentField = fields[0];
    const remainingFields = fields.slice(1);

    // Group data by the current field
    const groupsMap = data.reduce((acc, item) => {
      // Handle null/undefined values gracefully
      const key = item[currentField] != null ? item[currentField] : "Unknown";

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(item);
      return acc;
    }, {});

    // Convert the map to an array of group objects
    const result = Object.entries(groupsMap).map(([key, items]) => {
      const group = {
        groupKey: key,
        groupField: currentField,
        count: items.length,
      };

      // If there are more fields to group by, recursively group the items
      if (remainingFields.length) {
        group.children = this.groupDataByFields(items, remainingFields);
      } else {
        group.items = items;
      }

      return group;
    });

    return result;
  };

  /**
   * Get the grouped data structure
   * @returns {Array|null} The grouped data or null if no grouping is applied
   */
  getGroupedData = () => {
    return this.groupedData;
  };

  /**
   * Check if data is currently grouped
   * @returns {boolean} True if data is grouped
   */
  isGrouped = () => {
    return this.groupByFields.length > 0 && this.groupedData !== null;
  };

  /**
   * Handle sorting the data.
   * @param {string} column - The column to sort by.
   * @param {string} direction - The direction to sort ('asc' or 'desc').
   */
  handleSort = (column, direction) => {
    if (this.sortColumn === column && this.sortDirection === direction) {
      return;
    }
    this.sortColumn = column;
    this.sortDirection = direction === "asc" ? "asc" : "desc";

    // Sort the data first
    this.sortData();

    // If we have grouping, reapply it after sorting
    if (this.groupByFields.length > 0) {
      this.applyGroupBy();
    }
  };

  /**
   * Sort the filtered data.
   */
  sortData = () => {
    // Only sort if we have a column to sort by
    if (!this.sortColumn) {
      return;
    }

    this.filteredData.sort((a, b) => {
      const aValue = a[this.sortColumn];
      const bValue = b[this.sortColumn];

      // Handle null/undefined values for consistent sorting
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return this.sortDirection === "asc" ? -1 : 1;
      if (bValue == null) return this.sortDirection === "asc" ? 1 : -1;

      if (aValue < bValue) return this.sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    // Only update if we're not grouping (grouping will update the callback later)
    if (this.groupByFields.length === 0 && this.setFilteredData) {
      this.setFilteredData(this.filteredData);
    }
  };

  /**
   * Get the filtered data.
   * @returns {Array} The filtered data.
   */
  getData = () => {
    return this.filteredData;
  };

  /**
   * Handle string comparison operations
   * @param {string} rowValue - The value from the data row
   * @param {string} filterValue - The value to filter by
   * @param {string} operator - The operator to use
   * @returns {boolean} Whether the value matches the filter
   */
  static handleStringComparison(rowValue, filterValue, operator) {
    // Handle null/undefined values
    if (rowValue == null) {
      return operator === OPERATORS.EQUALS && filterValue === "";
    }

    switch (operator) {
      case OPERATORS.EQUALS:
        return rowValue === filterValue;
      case OPERATORS.CONTAINS:
        return rowValue.includes(filterValue);
      case OPERATORS.STARTS_WITH:
        return rowValue.startsWith(filterValue);
      case OPERATORS.ENDS_WITH:
        return rowValue.endsWith(filterValue);
      default:
        return true;
    }
  }
}
